import { useCallback, useRef } from 'react';

import { getAuth } from '@/db/credentials';
import {
  loadAccountSyncedSourceAuth,
  saveAccountSyncedSourceAuth,
} from '@/features/account/accountSourceAuth';
import { useAccountIdentity, useAccountToken } from '@/features/account';
import { PassphraseSession } from '@/lib/crypto';
import { getSource } from '@/sources/registry';
import type { ComicSource, SourceAuth } from '@/sources/types';

import { loadSourceAuth } from './loadSourceAuth';
import { deriveTwinSourceAuth } from './twinSourceAuth';

/**
 * Returns a stable function for loading decrypted auth for any source.
 *
 * Aggregate views cannot call `useSourceAuth` once per dynamic source, so they
 * use this lower-level loader and fan out from an effect.
 */
export function useSourceAuthLoader(
  opts: { deriveTwin?: boolean } = {},
): (source: ComicSource) => Promise<SourceAuth | undefined> {
  const deriveTwin = opts.deriveTwin ?? true;
  const getAccountToken = useAccountToken();
  const accountIdentity = useAccountIdentity();
  const currentAccountIdentity = useRef(accountIdentity);
  currentAccountIdentity.current = accountIdentity;

  return useCallback(
    async (source: ComicSource) => {
      const loadSaved = (target: ComicSource) =>
        loadSourceAuth(target, {
          isUnlocked: () => PassphraseSession.isUnlockedFor(accountIdentity),
          load: (id) => getAuth(id),
          loadAccountSynced: async (id) =>
            loadAccountSyncedSourceAuth(id, await getAccountToken(), accountIdentity),
        });
      const saveAuth = async (sourceId: string, auth: SourceAuth) => {
        const token = await getAccountToken();
        if (!token || !accountIdentity) return;
        await saveAccountSyncedSourceAuth(
          sourceId,
          auth,
          token,
          accountIdentity,
          () => currentAccountIdentity.current,
        );
      };
      try {
        const saved = await loadSaved(source);
        if (saved) {
          // Persist session cookies the source refreshed since this was saved.
          const reconciled = source.reconcileAuth?.(saved);
          if (!reconciled) return saved;
          void saveAuth(source.id, reconciled).catch(() => undefined);
          return reconciled;
        }
        if (!deriveTwin) return saved;
        // E-Hentai / ExHentai share one login: fill a missing side from its twin.
        return await deriveTwinSourceAuth(source, { getSource, loadAuth: loadSaved, saveAuth });
      } catch {
        // Auth availability should not break browsing/search. Source calls can
        // still proceed anonymously and surface their own typed errors.
        return undefined;
      }
    },
    [accountIdentity, deriveTwin, getAccountToken],
  );
}
